// The single source for the /features page: every section, every card, the
// detail each card opens, the limits table and the FAQs. Written for a club
// admin reading it: what stops being their problem, not what the code does.
//
// Card shape:
//   key     stable slug (also the ?feature= query value and the JSON-LD name)
//   title   the one-line claim on the card
//   blurb   one sentence under it
//   detail  bullets shown in the dialog (omit when `to` is a page that says it all)
//   to      a page the card opens instead of a dialog (formats pages)
//   more    an optional {to, label} link at the foot of the dialog
//   tag     'soon' | 'app'
//   keywords  the words people use for it, for the page's search only (never shown as our wording):
//             synonyms, old names, what other systems call it ("fixtures", "subs", "court booking")
//
// Keep it honest: every line here is live on web and app unless tagged. The
// commission number is the real one (StripeSettings.PlatformCommissionPercent).

export const COMMISSION_PERCENT = 2;

export const FEATURE_TAGS = {soon: 'Coming soon', app: 'In the app'};

export const FEATURE_LIMITS = [
  {label: 'Players', value: 'Unlimited'},
  {label: 'Admins', value: 'Unlimited'},
  {label: 'Competitions', value: 'Unlimited'},
  {label: 'Divisions', value: 'Unlimited'},
  {label: 'Players per division', value: 'Unlimited'},
  {label: 'Seasons', value: 'Unlimited'},
  {label: 'Entrants per event', value: 'Unlimited'},
  {label: 'Memberships and passes', value: 'Unlimited'},
  {label: 'Playing areas and bookings', value: 'Unlimited'},
  {label: 'Sports', value: 'All of them'},
  {label: 'Monthly fee', value: '£0.00'},
];

export const FEATURE_SECTIONS = [
  {
    key: 'setup',
    short: 'Setup',
    icon: 'mdi-rocket-launch-outline',
    title: 'Setting up a club',
    lead: 'Create the club, describe what you want to the setup assistant, import players, share an invite link.',
    items: [
      {
        key: 'create-club',
        keywords: ['new club', 'register a club', 'set up a club', 'club profile', 'logo', 'address', 'map', 'setup checklist', 'setup guide', 'goals', 'onboarding'],
        demo: 'clubPreview',
        icon: 'mdi-home-plus-outline',
        title: 'Create a club',
        blurb: 'Name, location, who can join and a logo. Tick what you will use Tap Leagues for and the setup guide shows only that.',
        detail: [
          'Address autocomplete and a map pin, so players can find you. The club runs on its own clock: times are shown in the club\'s time zone, labelled when a reader is away.',
          'Add your logo and the club page is ready to share.',
          'What will you use Tap Leagues for? Competitions, memberships and passes, bookings, a website widget or TV, payments. The setup guide on the admin dashboard covers only what you ticked.',
        ],
      },
      {
        key: 'setup-assistant',
        keywords: ['AI', 'assistant', 'setup wizard', 'help bot', 'chatbot', 'automatic setup', 'describe', 'price list', 'draft', 'apply', 'undo'],
        icon: 'mdi-creation-outline',
        title: 'Setup assistant',
        blurb: 'Describe the club in plain words, or paste a price list. It asks short questions and drafts the playing areas, competitions, memberships and booking rules.',
        to: '/features/setup-assistant',
        detail: [
          'Drafts playing areas, box leagues, ladders, tournaments, americano leagues, meets, memberships and passes, booking rules and scoring systems.',
          'Nothing is created until you check the draft and apply it. Every line of the draft opens in its own form, already filled in.',
          'Undo what it applied for 7 days, while nobody has joined, paid or booked.',
          'Names you type become players in the competition. Emails and phone numbers are never sent.',
          'On the website and in the app.',
        ],
      },
      {
        key: 'import-members',
        keywords: ['CSV', 'spreadsheet', 'Excel', 'upload members', 'bulk import', 'migrate', 'ClubSpark', 'Spond', 'Pitchero', 'import memberships', 'member list'],
        icon: 'mdi-file-upload-outline',
        title: 'Import players from a spreadsheet',
        blurb: 'Upload a CSV or spreadsheet. Columns are matched automatically and duplicates flagged. Memberships come in with them.',
        detail: [
          'Any CSV or spreadsheet. Names, emails, phone numbers, whatever you keep.',
          'Fix or skip rows inline before anything is created.',
          'Send invites as part of the import, or hold them until you are ready.',
          'A report of exactly what was added, skipped and merged.',
          'Moving from ClubSpark, Spond, Pitchero or your own list? The membership import brings each person\'s membership, dates and status too, with undo for 7 days.',
        ],
      },
      {
        key: 'no-email-members',
        keywords: ['managed players', 'players without email', 'add player by name', 'claim profile', 'guest players', 'juniors'],
        icon: 'mdi-account-plus-outline',
        title: 'Players without an email address',
        blurb: 'Add a player by name. They can claim the profile later with a link.',
        detail: [
          'Admin-managed players appear in every league, ladder and draw like anyone else.',
          'When they are ready, a claim link turns the profile into their own account with all their results.',
          'Mark juniors as you add them so the junior protections apply from day one.',
        ],
      },
      {
        key: 'join-links',
        keywords: ['join link', 'sign up link', 'signup link', 'registration', 'register players', 'invite', 'QR code', 'poster', 'link to join', 'password', 'approval'],
        icon: 'mdi-link-variant',
        title: 'Invite links and QR posters',
        blurb: 'One invite link per club and competition, with a printable QR poster.',
        detail: [
          'Every club and every competition has its own invite link and a printable QR poster.',
          'Open to anyone, or club players only. Instant, or each request approved by you.',
          'Add a password, a usage limit, start and end dates and a welcome message.',
          'Show the link on your public page, hide it, or show it to club players only.',
          'Regenerate it at any time and the old one stops working.',
        ],
      },
      {
        key: 'waiting-area',
        keywords: ['waiting list', 'waiting room', 'placement', 'new joiners', 'approve joiners', 'holding area', 'unplaced'],
        demo: 'placement',
        icon: 'mdi-account-clock-outline',
        title: 'Waiting area for new joiners',
        blurb: 'Hold joiners until an admin places them in a box, position or team.',
        detail: [
          'Suggested groupings for placing a full box of newcomers.',
          'People waiting can name the partners they would like to play with.',
          'Everyone is told when they have been placed.',
        ],
      },
    ],
  },
  {
    key: 'memberships',
    short: 'Memberships',
    icon: 'mdi-card-account-details-outline',
    title: 'Memberships and passes',
    lead: 'Players get and pay for them from the club page or the app. Renewals, applications, families and health questions handled.',
    items: [
      {
        key: 'memberships',
        keywords: ['subscriptions', 'subs', 'membership fees', 'annual fee', 'direct debit', 'renewals', 'season ticket', 'day pass', 'club membership', 'members', 'membership management', 'membership system', 'sign up for membership', 'dues'],
        icon: 'mdi-card-account-details-outline',
        title: 'Memberships and passes',
        blurb: 'Ongoing memberships that renew, passes for a block of sessions. Any competition can need one, or be cheaper with one.',
        to: '/features/memberships',
      },
      {
        key: 'membership-types',
        keywords: ['membership categories', 'junior membership', 'senior membership', 'family membership', 'social membership', 'student', 'instalments', 'monthly payments', 'joining fee', 'season pass', 'day pass', 'block of sessions', 'season ticket'],
        icon: 'mdi-shape-outline',
        title: 'Any kind of membership',
        blurb: 'Senior, Junior, Social, Family, Introductory, Season pass. Ongoing, set dates or repeating blocks, with instalments and a joining fee.',
        detail: [
          'Ongoing (renews every term), set dates (one term) or repeating blocks that roll on by themselves.',
          'Pay all at once, monthly or quarterly. An optional joining fee, taken once.',
          'Age limits worked out on the day they start or on a fixed date each year.',
          'For people new to the club, which renews as another type. Or one that goes with a child\'s membership.',
          'Live on the club page, live but hidden (by link only), closed to new people, draft or archived.',
        ],
      },
      {
        key: 'membership-renewals',
        keywords: ['subs', 'subscriptions', 'renewal', 'renew automatically', 'auto renew', 'recurring payment', 'direct debit', 'cancel membership', 'cooling off', 'refund', 'expiry', 'lapsed', 'age out'],
        icon: 'mdi-autorenew',
        title: 'Renewals and cooling-off',
        blurb: 'Charged when the term ends, with a reminder before. The 14-day right to cancel is built in.',
        detail: [
          'Renew automatically on a saved card, or off: it ends with the term.',
          'A reminder 3, 7 or 14 days before, by the length of the term.',
          'Members cancel themselves at the end of the term, or inside the 14-day cooling-off with a refund. Resume if they change their mind.',
          'Your refund rule: none beyond the law, full within a set number of days, or unused time.',
          'A failed renewal is retried, and the member gets a Pay now link.',
          'Juniors who age out are told and offered the next type. Nothing is charged until they apply.',
        ],
      },
      {
        key: 'membership-families',
        keywords: ['family membership', 'household', 'group membership', 'children', 'parents', 'family plan'],
        icon: 'mdi-account-group-outline',
        title: 'Families and groups',
        blurb: 'One purchase covers several people. The buyer names everyone and each person gets their own membership.',
        detail: [
          'Places with their own ages and limits: 1 to 2 adults, up to 4 children, or whatever you set.',
          'Add someone to the group mid-term at no extra charge until the next term.',
          'A person who ages out of their place is offered the next type.',
        ],
      },
      {
        key: 'membership-pay-for-someone',
        keywords: ['parent pays', 'guardian', 'consent', 'pay for my child', 'sponsor', 'pay link', 'under 18', 'junior consent'],
        icon: 'mdi-account-child-outline',
        title: 'Someone else pays',
        blurb: 'A parent, a partner or a sponsor pays by email link. No account needed.',
        detail: [
          'Under-18s need a parent or carer to agree by email before the club sees the application.',
          'The payer gets receipts, renewals and a link to change the card or stop paying.',
          'Stopping sends the next renewal to the member as a link to pay.',
        ],
      },
      {
        key: 'membership-applications',
        keywords: ['apply', 'application form', 'approve members', 'membership approval', 'questions', 'terms and conditions', 'waiver', 'health form', 'medical form', 'code of conduct'],
        icon: 'mdi-check-decagram-outline',
        title: 'Applications',
        blurb: 'Approve each application, or let people straight in. A paid application holds the card and charges on approval.',
        detail: [
          'Your own questions, conditions people tick, and your own terms or a Tap Leagues template.',
          'Health questions as a separate step, kept apart from everything else. Only admins you tick can open them, and every view is logged.',
          'Approve, approve and charge, or decline with a reason. Card holds last about 7 days.',
        ],
      },
      {
        key: 'membership-only',
        keywords: ['members only', 'member price', 'member discount', 'restricted entry', 'eligibility', 'entry requirements', 'non-members', 'guest fee'],
        icon: 'mdi-lock-open-check-outline',
        title: 'Members only, or a member price',
        blurb: 'A ladder, league, tournament, americano or meet can need a membership or pass, or cost less with one.',
        detail: [
          'Tick the types that count, or any membership or pass including ones you add later.',
          'A different entry price per membership. Players pay the lowest price any of theirs gives them.',
          'Bookings too: each membership has its own booking length, window, limit and price.',
          'A pass can cover a meet\'s sessions: the pass holder is in free, everyone else pays the session fee.',
          'The player list, club chat and name search can be kept for members, with a grace period.',
        ],
      },
      {
        key: 'membership-admin',
        keywords: ['subs', 'subscriptions', 'membership fees', 'member list', 'members list', 'membership register', 'who has paid', 'unpaid', 'arrears', 'cash payment', 'bank transfer', 'export members', 'download members', 'membership widget'],
        icon: 'mdi-clipboard-list-outline',
        title: 'Members and pass holders',
        blurb: 'Who holds what, renewing when, paid how. Add someone, record a cash payment, change a type, cancel or refund.',
        detail: [
          'Record a payment taken outside Tap Leagues, or give a type with no charge.',
          'Send a pay link when money is owed.',
          'Download the list as Excel or CSV. Health answers are never in it.',
          'Put your memberships and passes on your own website with a widget.',
        ],
      },
    ],
  },
  {
    key: 'bookings',
    short: 'Bookings',
    icon: 'mdi-timetable',
    title: 'Playing area bookings',
    lead: 'Players book a court, table or board in the hours you set, in the app and on your club page. Matches book their court too.',
    items: [
      {
        key: 'bookings',
        keywords: ['court booking', 'booking system', 'reserve a court', 'hire', 'court hire', 'book a court', 'book a table', 'book a pitch', 'reservations', 'slots', 'timetable', 'availability', 'online booking', 'court reservation', 'booking app'],
        icon: 'mdi-timetable',
        title: 'Book a court, table or board',
        blurb: 'A column per playing area, a row per slot. Tap a free slot, check the price, book. Paid or free.',
        to: '/features/bookings',
      },
      {
        key: 'booking-rules',
        keywords: ['opening hours', 'booking hours', 'slot length', 'cancellation policy', 'cut-off', 'how far ahead', 'booking limits', 'peak', 'prices per slot', 'booking window', 'freeze', 'maintenance', 'closed'],
        icon: 'mdi-tune-variant',
        title: 'Booking rules',
        blurb: 'Weekly hours, slot length, cancel cut-off, who can book, how long, how far ahead, how many at once and what it costs.',
        detail: [
          'Paint the weekly hours: booking needed, matches only, closed, or blank for open play.',
          'One set of rules for everything, or several: courts on the hour, match tables on 40 minutes.',
          'A row per group: no membership, each membership or pass, guests and visitors.',
          'Change one day from the calendar: open late, close early, close the day.',
          'Freeze a playing area for maintenance, a tournament or a private event.',
        ],
      },
      {
        key: 'booking-match',
        keywords: ['book a court for a match', 'hold a court', 'reserve for a match', 'fixtures', 'schedule', 'match booking', 'split the cost'],
        icon: 'mdi-calendar-check-outline',
        title: 'Matches book their court',
        blurb: 'Suggest a time and the court goes with it. Hold it while the other side replies.',
        detail: [
          'The schedule window shows the day\'s table. Pick a start and the booking covers the whole match.',
          'Hold the court for up to 2 days while you wait for a reply, if the club allows holds.',
          'A match further out than bookings open is booked for you the moment its day opens.',
          'One person pays, or the cost is split between the sides.',
          'Change the match time and the booking moves with it.',
        ],
      },
      {
        key: 'booking-visitors',
        keywords: ['visitors', 'non-members', 'pay and play', 'guest booking', 'public booking', 'book without an account', 'walk-in', 'guests'],
        icon: 'mdi-account-arrow-right-outline',
        title: 'Visitors book too',
        blurb: 'Switch visitors on and anyone can book from your website, with no account. A code by email, or a card.',
        detail: [
          'A name, an email and a mobile. A free booking is confirmed with a six-digit code, a paid one by card.',
          'One booking at a time for a visitor. They manage it from the link in their email.',
          'Guests added to a member\'s booking by name, with a guest fee if you set one.',
        ],
      },
      {
        key: 'booking-calendar',
        keywords: ['calendar', 'diary', 'admin bookings', 'book on behalf', 'block booking', 'coaching', 'private hire', 'no-show', 'move a booking', 'all bookings', 'export bookings'],
        icon: 'mdi-calendar-clock-outline',
        title: 'Booking calendar',
        blurb: 'The day on one screen. Book for a player or for the club, move a booking by dragging it, mark a no-show.',
        detail: [
          'Book on a player\'s behalf, or a club block for coaching or a private hire.',
          'Drag a booking to another time or playing area. Drag its edge to make it longer.',
          'Admins can always go over a rule; the booking carries a note saying which.',
          'All bookings in one list: search, filter by type, status and payment, download for the accounts.',
        ],
      },
      {
        key: 'booking-money',
        keywords: ['pay for a court', 'court fees', 'booking payment', 'refund', 'cancellation refund', 'reminder', 'release'],
        icon: 'mdi-cash-refund',
        title: 'Paid bookings and refunds',
        blurb: 'Charged when they book. A full refund before the cut-off, none after. The club cancels, the player is refunded in full.',
        detail: [
          'A price per slot per group of people. Members, pass holders and visitors can each pay their own.',
          'Cancel before the cut-off and the refund goes back to the card by itself.',
          'A reminder an hour before, with a one-tap Release if they can\'t make it.',
          'Live availability on your website and on Clubhouse TV.',
        ],
      },
    ],
  },
  {
    key: 'leagues',
    short: 'Box leagues',
    icon: 'mdi-table',
    title: 'Box leagues',
    lead: 'Divisions of any size, results entered by players, rollover to the next cycle.',
    items: [
      {
        key: 'box-leagues',
        keywords: ['box league', 'round robin', 'division', 'divisions', 'league', 'ladder league', 'monthly boxes', 'pyramid league', 'club league', 'league management', 'league software'],
        icon: 'mdi-table',
        title: 'Box leagues',
        blurb: 'Divisions of similar strength. Everyone plays everyone, then moves up or down.',
        to: '/formats/box-leagues',
      },
      {
        key: 'rollover',
        keywords: ['new season', 'next cycle', 'promotion', 'relegation', 'new round', 'reset the league', 'season rollover'],
        icon: 'mdi-swap-vertical-bold',
        title: 'Rollover to the next cycle',
        blurb: 'Promotion, relegation, dropouts and new joiners handled in one step.',
        detail: [
          'Everyone is listed in finishing order across all divisions.',
          'Choose how many move up and down. Adjust box sizes to fit who is left.',
          'Drag anyone to a different box before you confirm.',
          'Handicaps and settings carry over, and the old season stays viewable.',
        ],
      },
      {
        key: 'self-service-results',
        keywords: ['enter results', 'record scores', 'score entry', 'results', 'approve results', 'confirm result', 'standings', 'league table', 'points', 'bonus points', 'walkover'],
        demo: 'fixture',
        icon: 'mdi-scoreboard',
        title: 'Results entered by players',
        blurb: 'The opponent confirms, or results go straight in. Standings update immediately.',
        detail: [
          'Enter the score game by game or just the result, from the court.',
          'Opponent approval, admin approval or auto-approve, per competition.',
          'Bonus points for playing, winning or anything else you decide.',
          'A results grid and a standings table, live on web and app.',
        ],
      },
      {
        key: 'league-print',
        keywords: ['print', 'PDF', 'noticeboard', 'printable table', 'results grid', 'export'],
        demo: 'leagueMatrix',
        icon: 'mdi-printer-outline',
        title: 'Printable tables',
        blurb: 'A PDF of the standings or the results grid.',
        detail: [
          'Standings table or results matrix, per division or the whole cycle.',
          'Preview before you print.',
          'Juniors appear as first name and initial unless a parent has given permission.',
        ],
      },
    ],
  },
  {
    key: 'ladders',
    short: 'Ladders',
    icon: 'mdi-ladder',
    title: 'Ladders',
    lead: 'Challenge rules, expiry, movement and inactivity are all settings.',
    items: [
      {
        key: 'ladders',
        keywords: ['challenge ladder', 'king of the court', 'pyramid', 'ladder', 'rankings', 'challenge', 'climb', 'ladder league'],
        icon: 'mdi-ladder',
        title: 'Challenge ladders',
        blurb: 'Challenge players above you to move up. Runs continuously.',
        to: '/formats/ladders',
      },
      {
        key: 'ladder-rules',
        keywords: ['challenge rules', 'how far up', 'ladder settings', 'decline', 'forfeit', 'open challenges', 'doubles ladder'],
        demo: 'ladderRules',
        icon: 'mdi-tune-variant',
        title: 'Challenge rules',
        blurb: 'How far up or down a challenge can reach, how many can be open, what a decline costs.',
        detail: [
          'Challenge reach up and down, with separate rules for the top of the ladder.',
          'One challenge at a time, one sent plus any received, or no limit.',
          'A number of unrestricted challenges for players finding their level.',
          'Declining does nothing, drops the decliner, or counts as a forfeit.',
          'Several teams per player on a doubles ladder.',
        ],
      },
      {
        key: 'ladder-expiry',
        keywords: ['expire', 'expiry', 'time limit', 'unanswered challenge', 'deadline', 'warning'],
        icon: 'mdi-timer-sand',
        title: 'Challenge expiry',
        blurb: 'Challenges expire after a set number of days, with warnings at 24 hours and 1 hour.',
        detail: [
          'Set how many days a challenge stays open.',
          'Both players are warned before it expires.',
          'If a team leaves the ladder, its open challenges are cancelled and everyone is told.',
        ],
      },
      {
        key: 'ladder-inactivity',
        keywords: ['inactive players', 'inactivity', 'demotion', 'drop places', 'freeze', 'holiday', 'away', 'injured'],
        demo: 'ladderRules',
        icon: 'mdi-trending-down',
        title: 'Inactivity demotion',
        blurb: 'Players who have not played for a set number of days drop a set number of places, after a warning.',
        detail: [
          'Optional, per ladder. Choose the number of quiet days and how far they drop.',
          'A warning email goes out before anything moves.',
          'Freeze a player who is injured or away.',
        ],
      },
      {
        key: 'ladder-movement',
        keywords: ['swap places', 'winner moves up', 'ladder movement', 'positions'],
        demo: 'ladder',
        icon: 'mdi-swap-horizontal',
        title: 'Win movement',
        blurb: 'The winner moves above the loser, or the two swap places.',
        detail: [
          'Winner goes above the loser: the classic ladder.',
          'Swap places: gentler movement for a social ladder.',
        ],
      },
      {
        key: 'ladder-ai',
        keywords: ['prediction', 'chance of winning', 'odds', 'AI preview', 'win probability'],
        icon: 'mdi-crystal-ball',
        title: 'Challenge preview',
        blurb: 'An estimated chance of success for a challenge, based on both players\' results.',
        detail: [
          'Optional per ladder. Never shown for juniors.',
          'Takes handicaps into account where they are on.',
        ],
      },
    ],
  },
  {
    key: 'scheduling',
    short: 'Scheduling',
    icon: 'mdi-calendar-clock',
    title: 'Scheduling matches',
    lead: 'One side proposes a time and court, the other confirms. Reminders are automatic.',
    items: [
      {
        key: 'challenge-scheduling',
        keywords: ['challenge', 'propose a time', 'counter propose', 'court', 'schedule a challenge', 'time and court'],
        icon: 'mdi-sword-cross',
        title: 'Challenge with a time and court',
        blurb: 'A suggested date, time and court go with the challenge. Accept, decline or counter-propose.',
        detail: [
          'A suggested date, time and court go with the challenge.',
          'Accept as suggested, or accept with a different time.',
          'The challenger or an admin can change the suggestion later.',
          'Admins can create a challenge on anyone\'s behalf.',
        ],
      },
      {
        key: 'match-scheduling',
        keywords: ['fixtures', 'schedule', 'scheduling', 'match times', 'propose a time', 'arrange a match', 'court', 'when and where', 'diary', 'book a court'],
        demo: 'whenAndWhere',
        icon: 'mdi-calendar-check-outline',
        title: 'Schedule any match',
        blurb: 'One side proposes a date, time and playing area. The other side confirms. At a club that takes bookings, the court is booked with it.',
        detail: [
          'Pick a date, time and playing area from the match card.',
          'The other side gets a notification and confirms or counters.',
          'At a club that takes bookings, the schedule window shows the day\'s table: pick a start and the court is booked for the whole match, or held while the other side replies.',
          'Admins can set or clear a time for anyone.',
        ],
      },
      {
        key: 'match-reminders',
        keywords: ['reminders', 'notifications', 'nudge', 'result reminder', 'push notification', 'email reminder'],
        icon: 'mdi-bell-ring-outline',
        title: 'Match reminders',
        blurb: '24 hours and 2 hours before a match, and a reminder to enter the result afterwards.',
        detail: [
          'Match reminders at 24 hours and 2 hours.',
          'A reminder when a match has been talked about but not booked.',
          'A result reminder once the time has passed.',
        ],
      },
      {
        key: 'calendar',
        keywords: ['phone calendar', 'add to calendar', 'iCal', 'Google Calendar', 'Apple calendar'],
        icon: 'mdi-calendar-plus',
        title: 'Phone calendar',
        blurb: 'Add a match or challenge to the phone calendar from the app.',
        tag: 'app',
        detail: [
          'Challenges and scheduled matches go straight into the phone\'s calendar.',
        ],
      },
    ],
  },
  {
    key: 'tournaments',
    short: 'Tournaments',
    icon: 'mdi-trophy',
    title: 'Tournaments',
    lead: 'Knockout, or groups then knockout. Seeds, byes and third-place playoff.',
    items: [
      {
        key: 'tournaments',
        keywords: ['knockout', 'cup', 'bracket', 'draw', 'tournament', 'elimination', 'competition', 'championship', 'finals', 'groups', 'round robin groups'],
        icon: 'mdi-trophy',
        title: 'Tournaments',
        blurb: 'Knockout, or groups then knockout with a set number going through.',
        to: '/formats/tournaments',
      },
      {
        key: 'draw',
        keywords: ['draw', 'seeding', 'seeds', 'byes', 'bracket', 'generate draw', 'groups', 'third place', 'playoff', 'random draw'],
        demo: 'groups',
        icon: 'mdi-tournament',
        title: 'Draw generation',
        blurb: 'Seeding by hand, at random or by group finish. Byes and third-place playoff.',
        detail: [
          'Generate, regenerate or clear the draw at any point before play.',
          'Byes assigned for you, or by hand.',
          'Progress from the groups to the knockout with one tap.',
          'Optional third-place playoff.',
        ],
      },
      {
        key: 'bracket',
        keywords: ['bracket', 'draw view', 'knockout tree', 'public draw', 'champion'],
        demo: 'bracket',
        icon: 'mdi-family-tree',
        title: 'Bracket view',
        blurb: 'Zoom and pan on the web, swipe round by round in the app.',
        detail: [
          'Group tables and the bracket on one public page.',
          'A champion banner once the final is approved.',
        ],
      },
      {
        key: 'tournament-entry',
        keywords: ['enter a tournament', 'sign up', 'entry', 'entry fee', 'registration', 'entries', 'doubles entry'],
        icon: 'mdi-account-multiple-plus-outline',
        title: 'Entry by link',
        blurb: 'Players enter via the invite link until the draw is made.',
        detail: [
          'Entry fees can be collected as people enter.',
          'Members only, or a member price, when the club runs memberships.',
          'Team handicaps for doubles tournaments.',
        ],
      },
    ],
  },
  {
    key: 'americano',
    short: 'Americano',
    icon: 'mdi-account-switch',
    title: 'Americano leagues',
    lead: 'Individual entry, rotating partners, personal points.',
    items: [
      {
        key: 'americano',
        keywords: ['americano', 'mexicano', 'padel americano', 'social doubles', 'rotating partners', 'mixer', 'individual entry', 'solo entry'],
        icon: 'mdi-account-switch',
        title: 'Americano leagues',
        blurb: 'Groups of 4, 8 or 12. Pairings generated. Promotion and relegation between rounds.',
        to: '/formats/americano-leagues',
      },
      {
        key: 'americano-rotation',
        keywords: ['pairings', 'rotation', 'partners', 'whist', 'schedule', 'matches generated', 'replacement player', 'stand-in', 'substitute player'],
        demo: 'americanoRounds',
        icon: 'mdi-rotate-3d-variant',
        title: 'Pairings generated',
        blurb: 'Each player partners and faces every other player in the group.',
        detail: [
          'Whist rotation per division, so nobody sits out and nobody plays the same partner twice.',
          'Change the groups and the pairings are rebuilt.',
          'Swap in a sub mid-season without losing anyone\'s points.',
        ],
      },
      {
        key: 'americano-standings',
        keywords: ['individual standings', 'personal points', 'leaderboard', 'sets', 'games', 'promotion'],
        demo: 'americano',
        icon: 'mdi-podium',
        title: 'Personal standings',
        blurb: 'Points count for the individual. Ties broken on sets, then games.',
        detail: [
          'Standings per division, with promotion and relegation at the end of each cycle.',
          'A per-player preview of who moves where before you confirm the next round.',
          'A PDF of standings and rounds for the noticeboard.',
        ],
      },
    ],
  },
  {
    key: 'meets',
    short: 'Club nights',
    icon: 'mdi-handshake',
    title: 'Club nights and meets',
    lead: 'Recurring or one-off sessions with RSVP, a waitlist and matches generated on the day.',
    items: [
      {
        key: 'meets',
        keywords: ['social', 'club night', 'drop-in', 'session', 'sessions', 'RSVP', 'mixer', 'social night', 'turn up and play', 'casual play', 'club session', 'attendance'],
        icon: 'mdi-handshake',
        title: 'Meets',
        blurb: 'A session players join once and RSVP to per date.',
        to: '/formats/meets',
      },
      {
        key: 'rsvp',
        keywords: ['RSVP', 'sign up', 'attendance', 'waitlist', 'waiting list', 'capacity', 'spots', 'places', 'reminders', 'cancel'],
        demo: 'meet',
        icon: 'mdi-calendar-multiple-check',
        title: 'RSVP, waitlist and numbers',
        blurb: 'Minimum and maximum attendees, a waitlist, and reminders to people who have not answered.',
        detail: [
          'When a date is full, the next person to RSVP goes on the waitlist. When someone drops out, the next in line is offered the place automatically.',
          'Choose when RSVPs open: immediately, a number of days before, or at a set moment for first come first served.',
          'RSVPs close before the start so you can plan.',
          'A last-call reminder to anyone who has not answered while spots remain.',
          'If a date is under its minimum, you are alerted or it cancels itself.',
        ],
      },
      {
        key: 'meet-matches',
        keywords: ['matches on the night', 'pairings', 'balanced games', 'mix', 'fixtures', 'print matches'],
        demo: 'meetMatches',
        icon: 'mdi-shuffle-variant',
        title: 'Match generation',
        blurb: 'Matches made from attendees, at random or balanced by an ability order.',
        detail: [
          'Generate when you choose, when RSVPs close, or as soon as the date fills.',
          'Keep an ability order for the group and matches are balanced from it.',
          'Preview and edit the pairings before anyone sees them. Add a match on the day.',
          'Print the night\'s matches.',
        ],
      },
      {
        key: 'meet-day',
        keywords: ['register', 'walk-ins', 'no-shows', 'check in', 'attendance', 'register', 'day of'],
        demo: 'attendees',
        icon: 'mdi-clipboard-check-outline',
        title: 'Register',
        blurb: 'Walk-ins, no-shows, check-in and fee waivers on the day.',
        detail: [
          'Add a walk-in, mark a no-show, waive a fee, remove someone.',
          'Cancel, restore or move a date and everyone is told.',
          'Run a session without recording results if you just want the numbers.',
        ],
      },
    ],
  },
  {
    key: 'payments',
    short: 'Payments',
    icon: 'mdi-credit-card-outline',
    title: 'Payments',
    lead: 'Payments go to the club\'s own Stripe account. The only cost is 2% plus Stripe\'s fees, on payments taken through Tap Leagues. Open to clubs in the UK, the US, Canada, Australia and New Zealand.',
    items: [
      {
        key: 'own-account',
        keywords: ['Stripe', 'payments', 'payouts', 'bank account', 'merchant', 'take payments', 'card payments', 'online payments', 'payment processing', 'commission', 'fees', 'countries', 'currency', 'VAT', 'invoices'],
        icon: 'mdi-bank-outline',
        title: 'Club\'s own Stripe account',
        blurb: 'Onboarding inside Tap Leagues. Payouts on a schedule, or on demand.',
        detail: [
          'The club holds its own Stripe account. Tap Leagues does not hold the money.',
          `${COMMISSION_PERCENT}% commission plus Stripe's fees, only on payments taken through Tap Leagues. No per-entry fee, no setup fee.`,
          'Balance, payouts and payout schedule on the club dashboard.',
          'Open to clubs in the United Kingdom, the United States, Canada, Australia and New Zealand, in the club\'s own currency. More countries to follow.',
          'Fee estimates use your own country\'s Stripe rates. A VAT invoice for our commission where it applies.',
        ],
      },
      {
        key: 'wallets',
        keywords: ['Apple Pay', 'Google Pay', 'card', 'saved card', 'contactless', 'pay by card', 'failed payment', 'retry'],
        icon: 'mdi-contactless-payment',
        title: 'Apple Pay, Google Pay and card',
        blurb: 'Players save a card once and are charged after they play.',
        detail: [
          'Saved cards, a default card, one-tap payment.',
          'Players see exactly what will be charged and when.',
          'A failed card gets an "action needed" email and a retry.',
        ],
      },
      {
        key: 'session-fees',
        keywords: ['session fees', 'pay per session', 'court fees', 'club night fees', 'cancellation terms', 'terms and conditions', 'price change', 'weekly', 'monthly'],
        demo: 'tiers',
        icon: 'mdi-cash-clock',
        title: 'Session fees',
        blurb: 'Charged per session, weekly or monthly, with the club\'s own cancellation terms. A pass can cover the sessions.',
        detail: [
          'Your own cancellation terms: charge a percentage inside a cut-off, free before it.',
          'Nobody is charged for a late cancellation if someone else fills the spot.',
          'Your own terms and conditions, accepted before the first paid booking and again if you change them. Sample terms to start from.',
          'Change the price with notice, and players reconfirm.',
          'A booking confirmation email with the price and the terms.',
        ],
      },
      {
        key: 'entry-fees',
        keywords: ['entry fees', 'entry fee', 'competition fees', 'league fees', 'tournament fees', 'ladder fees', 'member price', 'members only', 'overdue', 'unpaid', 'cash', 'entry requirements'],
        icon: 'mdi-cash-check',
        title: 'Entry fees and entry requirements',
        blurb: 'A fee on any ladder, league, tournament or americano, agreed on joining. Members only, or a member price.',
        detail: [
          'Set a fee per competition or per season.',
          'Who can enter: anyone, or holders of a membership or pass you tick. A different price per membership.',
          'Changing a fee shows exactly who it reaches before you save. Apply it to current players too, or only new entrants.',
          'An overdue list, with waive, assign and settle-in-cash options.',
        ],
      },
      {
        key: 'bill-to',
        keywords: ['pay for someone', 'parent pays', 'bill to', 'family', 'guests', 'payer'],
        icon: 'mdi-account-child-outline',
        title: 'Pay for someone else',
        blurb: 'Assign a player\'s fees to another player, such as a parent. They accept first. Memberships can be paid by anyone with an email address.',
        detail: [
          'Works for guests and family members too.',
          'The payer accepts before anything is charged.',
          'A membership or pass can be paid by someone with no account at all: they get a link, a receipt and a page to change the card.',
        ],
      },
      {
        key: 'pay-link',
        keywords: ['payment link', 'pay link', 'pay online', 'pay without an account', 'invoice', 'outstanding'],
        icon: 'mdi-link-lock',
        title: 'Pay by link',
        blurb: 'A payment link that works without an account.',
        detail: [
          'For entry fees and outstanding charges.',
          'Saved cards work on the link too.',
        ],
      },
      {
        key: 'refunds-receipts',
        keywords: ['refunds', 'refund', 'receipts', 'receipt', 'statement', 'Excel', 'accounts', 'bookkeeping', 'invoices', 'VAT', 'export payments'],
        icon: 'mdi-receipt-text-outline',
        title: 'Refunds, receipts and statements',
        blurb: 'Full or partial refunds, a receipt for every payment, an Excel statement.',
        detail: [
          'Refund one line, a custom amount, or every charge for a date. Bookings cancelled before the cut-off refund themselves.',
          'PDF receipts and receipt emails for every payment.',
          'An Excel statement for any period, or for one competition.',
        ],
      },
    ],
  },
  {
    key: 'handicaps',
    short: 'Handicaps',
    icon: 'mdi-scale-balance',
    title: 'Handicaps',
    lead: 'Handicaps in the sport\'s own units, shown on every match.',
    items: [
      {
        key: 'handicap-schemes',
        keywords: ['handicap', 'handicaps', 'handicapping', 'rating', 'levels', 'start', 'owe and receive', 'points per game', 'fair matches', 'balance'],
        demo: 'handicapChips',
        icon: 'mdi-scale-balance',
        title: 'Handicap schemes per sport',
        blurb: 'Points per game, shots, goals, frames or owe-and-receive, or custom units.',
        detail: [
          'Tennis fifteens, squash and badminton points, snooker frame points, darts start reduction, bowls shots, football goals, or your own units.',
          'Per ladder, league, tournament or americano.',
          'Show the values to players or keep them to admins.',
        ],
      },
      {
        key: 'handicap-start',
        keywords: ['handicap start', 'match card', 'calculator', 'who starts', 'fifteen love', 'head start'],
        demo: 'handicap',
        icon: 'mdi-flag-checkered',
        title: 'Start shown on every match',
        blurb: 'The resolved start for any pairing, on the match card and in live scoring.',
        detail: [
          'Worked out for any pairing, even before a match exists.',
          'Loaded into live scoring automatically.',
          'A calculator to see what any two players would start at.',
        ],
      },
      {
        key: 'handicap-adjust',
        keywords: ['automatic handicap', 'adjust', 'rating changes', 'history', 'provisional', 'cap', 'bulk set'],
        demo: 'handicapHistory',
        icon: 'mdi-auto-fix',
        title: 'Automatic adjustment',
        blurb: 'Handicaps move after each result, stepped or proportional, or are set by hand.',
        detail: [
          'Stepped or proportional adjustment after each result.',
          'A cap on how far a handicap can move per match, and provisional matches for newcomers.',
          'Bulk set a whole competition, and a full history of every change.',
        ],
      },
    ],
  },
  {
    key: 'scoring',
    short: 'Scoring',
    icon: 'mdi-scoreboard-outline',
    title: 'Results and scoring',
    lead: 'Result entry, scoring systems per sport, live scoring, match events.',
    items: [
      {
        key: 'scoring-systems',
        keywords: ['scoring', 'scoring system', 'best of', 'sets', 'games', 'frames', 'legs', '501', 'X01', 'ends', 'custom scoring', 'points'],
        icon: 'mdi-counter',
        title: 'Scoring systems',
        blurb: 'Around 60 presets across the sports, plus custom systems and per-competition overrides.',
        detail: [
          'Best-of sets and games, points per game, frames, legs, X01, ends. Whatever your sport uses.',
          'Clubs build custom systems from a preset, with a preview.',
          'Override the points and bonuses per competition.',
        ],
      },
      {
        key: 'live-scoring',
        keywords: ['live score', 'live scoring', 'scoreboard', 'point by point', 'spectator', 'offline', 'umpire', 'referee'],
        demo: 'liveScore',
        icon: 'mdi-broadcast',
        title: 'Live scoring',
        blurb: 'Point-by-point scoring from the court, a spectator scoreboard, offline support.',
        detail: [
          'A scoreboard anyone can open from a share link.',
          'Scores queue while offline and upload when you are back in range.',
          'Hand the scoring over to someone else mid-match.',
          'A Live now section on everyone\'s home screen.',
        ],
      },
      {
        key: 'match-events',
        keywords: ['180s', 'checkouts', 'goals', 'assists', 'leaderboards', 'player of the match', 'stats'],
        icon: 'mdi-bullseye-arrow',
        title: 'Match events',
        blurb: '180s, checkouts, goals and assists per player, with leaderboards.',
        detail: [
          'Per-player events chosen by the sport.',
          'Most 180s this season, top scorer, best checkout. Automatic leaderboards.',
        ],
      },
      {
        key: 'ai-reports',
        keywords: ['AI', 'match report', 'summary', 'write up', 'commentary'],
        icon: 'mdi-text-box-check-outline',
        title: 'AI match reports',
        blurb: 'A written summary of each match. Opt-in per competition.',
        detail: [
          'Opt-in per competition. Never for juniors.',
        ],
      },
    ],
  },
  {
    key: 'stats',
    short: 'Stats',
    icon: 'mdi-chart-box-outline',
    title: 'Stats',
    lead: 'Records and form for every player, club and competition.',
    items: [
      {
        key: 'player-stats',
        keywords: ['stats', 'statistics', 'records', 'form', 'streaks', 'win rate', 'profile', 'rating', 'Tap Leagues score', 'history'],
        demo: 'stats',
        icon: 'mdi-account-star-outline',
        title: 'Player stats',
        blurb: 'Record, streaks, average position, most played, most beaten.',
        detail: [
          'Per club and across all your clubs.',
          'A Tap Leagues score that moves with your results.',
          'A public profile page for every player.',
        ],
      },
      {
        key: 'competition-stats',
        keywords: ['stats', 'statistics', 'leaderboard', 'form table', 'most improved', 'league stats'],
        demo: 'stats',
        icon: 'mdi-chart-timeline-variant',
        title: 'Competition stats',
        blurb: 'Form tables and leaderboards for each league, ladder, tournament, americano and meet.',
      },
    ],
  },
  {
    key: 'communication',
    short: 'Communication',
    icon: 'mdi-bullhorn-outline',
    title: 'Notifications, email and chat',
    lead: 'Push and email per notification type, announcements, bulk email, chat.',
    items: [
      {
        key: 'notifications',
        keywords: ['notifications', 'push', 'push notifications', 'email alerts', 'alerts', 'settings', 'time zone', 'timezone'],
        demo: 'notifications',
        icon: 'mdi-bell-outline',
        title: 'Notifications',
        blurb: 'Push and email, switchable per type.',
        detail: [
          'Around 45 kinds, each with its own push and email switch for every player.',
          'Admin alerts for join requests, leavers, placements and results to approve.',
          'Times on the club\'s clock everywhere, labelled once on the screen when a reader is in another time zone.',
        ],
      },
      {
        key: 'announcements',
        keywords: ['announcements', 'news', 'notice', 'pinned message', 'club news', 'schedule a post', 'broadcast'],
        icon: 'mdi-bullhorn-outline',
        title: 'Announcements',
        blurb: 'Pinned to every player\'s feed, with an image and a link. Can be scheduled and expire.',
      },
      {
        key: 'email-members',
        keywords: ['email', 'bulk email', 'newsletter', 'mailing list', 'email everyone', 'email the club', 'mailshot', 'unsubscribe'],
        icon: 'mdi-email-multiple-outline',
        title: 'Email players',
        blurb: 'The whole club, one competition or one division. Unsubscribes handled.',
        detail: [
          'Filter by club, league, cycle, division, ladder, tournament, meet or americano.',
          'Your club\'s name and footer on every email.',
        ],
      },
      {
        key: 'email-preferences',
        keywords: ['unsubscribe', 'email settings', 'opt out', 'email preferences', 'marketing emails'],
        icon: 'mdi-email-check-outline',
        title: 'Email preferences',
        blurb: 'Players manage their own, from any email, without signing in.',
        detail: [
          'One-click unsubscribe that mail apps recognise.',
          'Account, booking and payment emails always arrive.',
        ],
      },
      {
        key: 'chat',
        keywords: ['chat', 'messaging', 'messages', 'group chat', 'direct message', 'DM', 'WhatsApp', 'team chat', 'mute', 'read receipts'],
        demo: 'chat',
        icon: 'mdi-chat-outline',
        title: 'Chat',
        blurb: 'A chat for every club, competition, division and challenge, membership kept in sync.',
        detail: [
          'Players are added and removed as the competition changes.',
          'Direct and group chats too, with photos, typing and read receipts.',
          'Mute any chat. Delete your own message for everyone.',
          'Unread messages digested into one email if someone is away from the app.',
        ],
      },
    ],
  },
  {
    key: 'safety',
    short: 'Safety',
    icon: 'mdi-shield-check-outline',
    title: 'Approvals, safety and privacy',
    lead: 'Approvals in one place, privacy for clubs and players, message reporting, blocking, junior protections.',
    items: [
      {
        key: 'approvals',
        keywords: ['approve', 'approvals', 'pending', 'requests', 'join requests', 'moderation', 'admin queue', 'membership applications'],
        icon: 'mdi-check-decagram-outline',
        title: 'Approvals',
        blurb: 'Results, join requests, placements and membership applications waiting for an admin, in one place.',
      },
      {
        key: 'reports',
        keywords: ['report a message', 'abuse', 'moderation', 'report', 'safeguarding', 'flag'],
        icon: 'mdi-flag-outline',
        title: 'Message reports',
        blurb: 'Players can report a message. Admins see and act on reports.',
        detail: [
          'Reports go to the club\'s admins, or to Tap Leagues where that is right.',
          'A reports queue on the admin dashboard.',
        ],
      },
      {
        key: 'blocking',
        keywords: ['block', 'blocking', 'block a player', 'harassment', 'safety'],
        icon: 'mdi-account-cancel-outline',
        title: 'Blocking',
        blurb: 'A blocked player cannot message you.',
      },
      {
        key: 'juniors',
        keywords: ['juniors', 'under 18', 'children', 'kids', 'safeguarding', 'child protection', 'privacy', 'parents', 'consent'],
        icon: 'mdi-human-child',
        title: 'Junior protections',
        blurb: 'Under-18s are never public and can\'t be followed. No AI previews involving a junior.',
        detail: [
          'Shown as a private player, or first name and initial, to anyone outside their club.',
          'Left off a public club\'s player list for people outside the club.',
          'Shortened names on printed PDFs unless a parent has given permission.',
          'No AI previews or reports involving a junior.',
        ],
      },
      {
        key: 'privacy',
        keywords: ['private club', 'club privacy', 'public club', 'visibility', 'hidden', 'discoverable', 'find a club', 'GDPR'],
        icon: 'mdi-eye-off-outline',
        title: 'Club privacy',
        blurb: 'Club players only by default, or public. Each competition can choose its own.',
        detail: [
          'Outsiders see the club name, logo and how to join, nothing more.',
          'Choose whether the club appears in Find a club.',
          'Preview your club page exactly as a visitor sees it.',
        ],
      },
      {
        key: 'member-privacy',
        keywords: ['player privacy', 'profile visibility', 'private profile', 'hide my name', 'GDPR', 'data'],
        icon: 'mdi-account-lock-outline',
        title: 'Player privacy',
        blurb: 'Each player chooses who outside their clubs can see their profile.',
        detail: [
          'Public, club players only, or hidden as a private player.',
          'Full names and photos stay inside the club unless a player chooses otherwise.',
          'Club admins always see their own players\' details.',
        ],
      },
      {
        key: 'playing-areas',
        keywords: ['courts', 'tables', 'boards', 'pitches', 'venues', 'playing areas', 'facilities', 'rinks', 'lanes', 'court names'],
        icon: 'mdi-map-marker-outline',
        title: 'Playing areas',
        blurb: 'Courts, tables and boards, assignable to matches and challenges, and bookable in the hours you set.',
      },
    ],
  },
  {
    key: 'showcase',
    short: 'Showcase',
    icon: 'mdi-television-play',
    title: 'Public pages, widgets and TV',
    lead: 'Public pages, website widgets, a clubhouse screen and PDFs.',
    items: [
      {
        key: 'public-pages',
        keywords: ['public page', 'club page', 'website', 'share', 'link preview', 'SEO', 'public results', 'club website page'],
        icon: 'mdi-web',
        title: 'Public pages',
        blurb: 'A page for the club, every competition, every player and every match, public when you choose.',
        detail: [
          'Share any of them and the link shows a proper preview.',
          'Public or club players only, for the club and for each competition.',
        ],
      },
      {
        key: 'widgets',
        keywords: ['embed', 'widget', 'website', 'iframe', 'embed code', 'standings on our website', 'results on our website', 'booking widget', 'membership widget', 'WordPress', 'Wix', 'Squarespace'],
        demo: 'widget',
        icon: 'mdi-widgets-outline',
        title: 'Website widgets',
        blurb: 'Standings, ladders, draws, results, stats, booking availability and memberships embedded on the club website.',
        detail: [
          'Book: free slots for the next few days. Availability: what each playing area is doing right now. Get a membership or pass: your live types with a Get it button.',
          'Your brand colour, standard or compact.',
          'Locked to the websites you allow.',
          'Updates itself.',
        ],
      },
      {
        key: 'clubhouse-tv',
        keywords: ['TV', 'screen', 'display', 'clubhouse screen', 'big screen', 'digital signage', 'scoreboard', 'monitor', 'smart TV', 'Chromecast', 'Fire Stick', 'live results on a screen'],
        icon: 'mdi-television-play',
        title: 'Clubhouse TV',
        blurb: 'A rotating screen with live scores, results, tables and a join QR code.',
        to: '/features/clubhouse-tv',
        detail: [
          'Fourteen panels you can show, hide, pin and order, including what every playing area is doing right now.',
          'Result celebrations and a ticker.',
          'Light or dark, your accent colour.',
          'Any browser on any TV. Nothing to install.',
          'Set up from the website or the app.',
        ],
      },
      {
        key: 'pdf',
        keywords: ['PDF', 'print', 'printable', 'export', 'download', 'noticeboard'],
        icon: 'mdi-file-pdf-box',
        title: 'PDF export',
        blurb: 'Tables, grids, ladders and brackets as PDFs.',
      },
    ],
  },
  {
    key: 'members',
    short: 'Players',
    icon: 'mdi-cellphone-link',
    title: 'For players',
    lead: 'Web and app, one home screen, sign-in options, friends in other clubs.',
    items: [
      {
        key: 'web-and-app',
        keywords: ['app', 'mobile app', 'iPhone', 'iOS', 'Android', 'iPad', 'website', 'browser', 'offline', 'sync', 'download the app'],
        demo: 'homeScreen',
        icon: 'mdi-cellphone-link',
        title: 'Web and app',
        blurb: 'iPhone, Android and any browser, with push notifications on the phone.',
        detail: [
          'Live updates everywhere: a result entered on a phone appears on the web the same second.',
          'The app keeps the feed and chats for reading offline.',
        ],
      },
      {
        key: 'home-screen',
        keywords: ['home', 'dashboard', 'feed', 'timeline', 'what needs attention', 'book a court', 'upcoming matches'],
        demo: 'homeScreen',
        icon: 'mdi-home-outline',
        title: 'Home screen',
        blurb: 'What needs attention, a Book a court button, live matches, upcoming matches, competitions and the activity feed.',
      },
      {
        key: 'sign-in',
        keywords: ['login', 'log in', 'sign in', 'Google', 'Apple', 'passkey', 'password', 'account', 'combine accounts', 'duplicate account'],
        demo: 'signIn',
        icon: 'mdi-login-variant',
        title: 'Sign in',
        blurb: 'Email, Google, Apple or a passkey. Two accounts can be combined.',
      },
      {
        key: 'two-step',
        keywords: ['2FA', 'two factor', 'MFA', 'authenticator', 'security', 'recovery codes', 'passkey'],
        icon: 'mdi-shield-key-outline',
        title: 'Two-step sign-in',
        blurb: 'An authenticator app code or a passkey as a second step, with recovery codes.',
      },
      {
        key: 'find-friends',
        keywords: ['search', 'find players', 'user tag', 'username', 'friends', 'find a club'],
        icon: 'mdi-account-search-outline',
        title: 'Find friends',
        blurb: 'A user tag for every player, and one search for clubs or people.',
      },
      {
        key: 'following',
        keywords: ['follow', 'followers', 'friends', 'connect', 'other clubs', 'network'],
        icon: 'mdi-account-multiple-plus-outline',
        title: 'Following',
        blurb: 'Follow friends in other clubs. Both agree, then see each other\'s profile and can message.',
      },
    ],
  },
  {
    key: 'integrations',
    short: 'Integrations',
    icon: 'mdi-connection',
    title: 'Integrations',
    lead: 'Rating services and the tools the club already uses.',
    items: [
      {
        key: 'squash-levels',
        keywords: ['Squash Levels', 'SquashLevels', 'levels', 'rating', 'ranking', 'export results', 'England Squash'],
        icon: 'mdi-chart-line',
        title: 'Squash Levels',
        blurb: 'Results sent automatically for players who opt in. Levels shown on profiles.',
        detail: [
          'Per competition and per player consent.',
          'Juniors are never sent.',
        ],
      },
      {
        key: 'external-ratings',
        keywords: ['WTN', 'DUPR', 'UTR', 'LTA', 'rating', 'rankings', 'world tennis number', 'ratings sync'],
        icon: 'mdi-star-half-full',
        title: 'External ratings',
        blurb: 'WTN, DUPR and other rating services, synced from results.',
        tag: 'soon',
      },
      {
        key: 'booking',
        keywords: ['booking system', 'court booking', 'integration', 'ClubSpark', 'Playtomic', 'MyCourts', 'existing booking system', 'reserve a court', 'hire'],
        icon: 'mdi-timetable',
        title: 'Booking systems',
        blurb: 'Tap Leagues books your playing areas itself: a booking page, visitor bookings and a widget for your website. Or keep the system you have and use it for matches.',
        to: '/features/bookings',
      },
      {
        key: 'assistant',
        keywords: ['AI', 'chatbot', 'assistant', 'help bot', 'help', 'support', 'FAQ', 'how do I', 'virtual assistant', 'AI helper', 'Tappy', 'ask a question'],
        icon: 'mdi-chat-question-outline',
        title: 'Tappy, our AI helper',
        blurb: 'Answers questions about using Tap Leagues, in your language, for players and admins. It never changes your club; it hands admins to the setup assistant when they want something built.',
        to: '/features/setup-assistant',
      },
    ],
  },
];

export const FEATURE_FAQS = [
  {
    q: 'What does it cost?',
    a: `Nothing to use. If your club takes payments through Tap Leagues, ${COMMISSION_PERCENT}% commission plus Stripe's fees comes out of each payment. No per-entry fee and no setup fee.`,
  },
  {
    q: 'Can players book courts?',
    a: 'Yes. Switch bookings on, paint the hours, and players book a court, table or board from the app, the club page or a widget on your website. Visitors can book too, with no account. Matches book their court as part of arranging the time.',
  },
  {
    q: 'Can we run our memberships on it?',
    a: 'Yes. Memberships that renew and passes for a block of sessions, paid by card from the club page or the app. Families, juniors with a parent\'s agreement, applications you approve, health questions and an import from your current list. Any competition or booking can need one, or cost less with one.',
  },
  {
    q: 'Are there really no limits?',
    a: 'None. Unlimited players, admins, competitions, divisions, players per division and seasons, on every club.',
  },
  {
    q: 'Which sports?',
    a: 'Squash, padel, tennis, pickleball, badminton, table tennis, racketball, darts, snooker, pool, bowls, football, foosball, fencing, basketball and board games, each with its own scoring. If yours is missing, ask.',
  },
  {
    q: 'Do players need the app?',
    a: 'No. Everything works in a browser. The app adds push notifications, offline chat and the phone calendar, and most players end up using it.',
  },
  {
    q: 'Who holds the money?',
    a: 'Your club does. Payments land in the club\'s own Stripe account, set up from inside Tap Leagues. Tap Leagues never holds your funds.',
  },
  {
    q: 'Which countries can take payments?',
    a: 'Clubs in the United Kingdom, the United States, Canada, Australia and New Zealand, in their own currency. More countries are on the way. Everything else works everywhere.',
  },
  {
    q: 'Can the assistant set it all up?',
    a: 'Mostly. Describe the club in plain words, or paste a price list, and the setup assistant drafts the playing areas, competitions, memberships and passes and booking rules. You check the draft and apply it; nothing is live before that, and you can undo for 7 days. Publishing a paid membership, switching bookings on and sharing invite links stay yours to do.',
  },
  {
    q: 'How long does setup take?',
    a: 'A club and a first competition in about ten minutes. Describe the club to the setup assistant or fill in the forms, import your players from a spreadsheet and share an invite link, and the rest fills itself in.',
  },
];

export const FEATURE_ITEMS = FEATURE_SECTIONS.flatMap(section =>
    section.items.map(item => ({...item, section: section.key})));

export const findFeature = (key) => FEATURE_ITEMS.find(item => item.key === key) || null;
